import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { redirectToPayment } from '../../services/stripeService';
import '../../components/subscriptions/Subscriptions.css';

export default function SubscriptionPlansSection() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const plansSectionRef = useRef(null);

  // Helper function to check if user has an active subscription (excluding trialing)
  // Users can subscribe when trialing or when there's no active subscription
  const hasActiveSubscription = () => {
    if (!user?.subscription) return false;
    
    const subscription = user.subscription;
    const status = subscription.status || 'expired';
    const currentPeriodEnd = subscription.currentPeriodEnd;
    const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd || false;
    
    // Allow subscription during trialing period
    if (status === 'trialing') {
      return false; // Users can subscribe while trialing
    }
    
    // If we have a period end date, check if it's in the future
    if (currentPeriodEnd) {
      const periodEndDate = new Date(currentPeriodEnd);
      const now = new Date();
      
      // If period hasn't ended yet and status is active or cancelled (but still within period)
      if (periodEndDate > now) {
        // Only block if status is active or cancelled (not trialing)
        if (status === 'active' || status === 'canceled' || status === 'cancelled' || cancelAtPeriodEnd) {
          return true; // Active or cancelled but still within period - block subscription
        }
      }
    }
    
    // Check if status indicates active subscription (excluding trialing)
    return status === 'active' || 
           ((status === 'canceled' || status === 'cancelled') && currentPeriodEnd && new Date(currentPeriodEnd) > new Date());
  };

  const isDisabled = hasActiveSubscription();

  // Scroll-triggered animations for subscription plans section
  useEffect(() => {
    const plansSection = plansSectionRef.current;
    if (!plansSection) return;

    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          plansSection.classList.add('visible');
          
          // Animate checkmark paths with staggered delays
          const checkmarkPaths = plansSection.querySelectorAll('.checkmark-path');
          checkmarkPaths.forEach((path, index) => {
            setTimeout(() => {
              path.style.animation = 'checkmarkDraw 0.6s ease-out forwards';
            }, 500 + (index * 100));
          });
          
          observer.unobserve(plansSection);
        }
      });
    }, observerOptions);

    observer.observe(plansSection);

    return () => {
      if (plansSection) {
        observer.unobserve(plansSection);
      }
    };
  }, []);

  // Handle subscribe button click
  const handleSubscribe = (plan) => {
    // Don't allow subscription if user already has an active subscription
    if (isDisabled) {
      return;
    }
    
    console.log('[SubscriptionPlansSection] Subscribe button clicked for plan:', plan.type);
    
    // Check if user is authenticated
    if (!isAuthenticated || !user || !user.uid) {
      // Redirect to signup first
      navigate('/signup');
      return;
    }
    
    // Use the centralized service function with return URL
    const returnUrl = `${window.location.origin}/account`;
    const result = redirectToPayment(plan.type, user.uid, returnUrl);
    
    if (!result.success) {
      console.error('[SubscriptionPlansSection] Failed to redirect to payment:', result.error);
      navigate('/account');
    }
  };

  const plans = t('subscriptions.plans', { returnObjects: true }) || [];
  
  // Filter out free trial and daily plans (daily plan has been removed)
  const paidPlans = plans.filter(plan => plan.type !== 'free-trial' && plan.type !== 'daily');

  return (
    <div 
      className={`subscription-plans-section account-subscription-plans ${isDisabled ? 'disabled' : ''}`}
      ref={plansSectionRef}
      style={isDisabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
    >
      <div className="subscription-plans-container">
        <h2 className="text-xl font-semibold text-gray-300 mb-6">
          {t('account.subscriptionPlans.title', { defaultValue: 'Subscription Plans' })}
        </h2>
        {isDisabled && (
          <div className="mb-4 p-3 rounded-lg bg-gray-800/50 border border-gray-700">
            <p className="text-gray-400 text-sm text-center">
              {i18n.language === 'es' 
                ? 'Tienes una suscripción activa. Gestiona tu suscripción desde la sección de Estado de Suscripción.' 
                : 'You have an active subscription. Manage your subscription from the Subscription Status section.'}
            </p>
          </div>
        )}
        <div className="plans-grid account-plans-grid">
          {paidPlans.map((plan, index) => (
            <div 
              key={index}
              className={`plan-card ${plan.featured ? 'featured' : ''} ${isDisabled ? 'disabled' : ''}`}
              style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              <div className="plan-card-content">
                {plan.tag && (
                  <span className={`plan-tag ${plan.tag === 'Premium' ? 'plan-tag-premium' : ''}`}>
                    {plan.tag}
                  </span>
                )}
                <h2 
                  className="plan-title"
                  dangerouslySetInnerHTML={{ __html: plan.title }}
                />
                <div className="plan-price">
                  {plan.price}
                  {plan.pricePeriod && (
                    <span className="plan-price-period">{plan.pricePeriod}</span>
                  )}
                </div>
                {plan.description && (
                  <p className="plan-description">{plan.description}</p>
                )}
                <ul className="plan-features">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="plan-feature-item">
                      <svg 
                        className="checkmark-icon" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="3" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path 
                          className="checkmark-path" 
                          d="M20 6L9 17l-5-5"
                        />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.buttonLink ? (
                  <button 
                    className="plan-button"
                    onClick={() => handleSubscribe(plan)}
                    disabled={isDisabled}
                    style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    {plan.buttonText}
                  </button>
                ) : (
                  <button 
                    className="plan-button"
                    onClick={() => handleSubscribe(plan)}
                    disabled={isDisabled}
                    style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    {plan.buttonText}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="text-gray-400 text-sm text-center mt-6">
          {t('account.subscriptionPlans.priceLegend', { defaultValue: 'Prices are expressed in US Dollars' })}
        </p>
      </div>
    </div>
  );
}

